import {ChangeEvent, FC, useContext, useEffect} from 'react';
import {useRecoilState, useRecoilStateLoadable, useRecoilValue, useSetRecoilState} from 'recoil';

import ServiceRequestsTable from './ServiceRequestsTable';
import {Paper} from '@mui/material';
import {searchAddressItemStrategy} from '../../../api/strategies/searchAddressItemStrategy';
import Filters from '../../Filters';

import {useDebouncePageable} from '../../../hooks/useDebouncePageable';

import {assignableServiceRequestsState, serviceRequestToAssignPageable} from '../../../store/service-requests/atoms';
import {
  currentServiceRequestsFiltersSetState,
  currentServiceRequestsToAssignFiltersSetState,
  filtersServiceRequestsSetsState,
  filtersServiceRequestsState,
  filtersServiceRequestsToAssignState,
  triggerFiltersServiceRequestsQuery,
} from '../../../store/filters/atoms';

import {filterServiceRequestsBy, statusCodesAssign} from '../../Filters/constants/filters.data';

import {IPageableTable} from '../../../shared/types/pageable.types';
import {filtersSetsServiceRequestsQuery} from '../../../store/filters/selectors';
import {EventBusContext} from '../../../providers/EventBus';
import {taskLocationsMapWithNamesState} from '../../../store/taskLocations/atoms';
import {taskLocationsDictQuery} from '../../../store/taskLocations/selectors';
import {ITaskLocationsDict} from '../../../shared/types/taskLocationsDict.types';
import {statusCodeTranslation} from '../../../shared/status.data';
import {searchWorkersForFilterStrategy} from '../../../api/strategies/searchWorkersStrategy';
import {loadableWithDefault} from '../../Filters/helpers/loadableWithDefault';
import {IDeviceTypesDict} from '../../../shared/types/deviceTypesDict.types';
import {deviceTypesDictQuery} from '../../../store/deviceTypes/selectors';

export const FiltersAndTableContainer: FC = () => {
  const [pageable, setPageable] = useRecoilState(serviceRequestToAssignPageable);
  const {
    pageableToDebounce: {page, size},
    handleChangePage,
    handleChangeRowsPerPage,
    resetPageable,
  } = useDebouncePageable<IPageableTable>(pageable, setPageable);
  const setAssignable = useSetRecoilState(assignableServiceRequestsState);
  const triggerQuery = useSetRecoilState(triggerFiltersServiceRequestsQuery);

  const setFilters = useSetRecoilState(filtersServiceRequestsToAssignState);
  const setCurrentFilters = useSetRecoilState(currentServiceRequestsToAssignFiltersSetState);
  const filtersSets = useRecoilValue(filtersServiceRequestsState);
  const currFiltersSet = useRecoilValue(currentServiceRequestsFiltersSetState);

  const [{contents, state}, setFiltersSets] = useRecoilStateLoadable(filtersSetsServiceRequestsQuery);
  const [deviceTypesLoadable] = useRecoilStateLoadable(deviceTypesDictQuery);
  const [{state: taskLocationsState, contents: taskLocationsContents}, setTaskLocations] =
    useRecoilStateLoadable(taskLocationsDictQuery);
  const mapWithTaskLocations = useRecoilValue(taskLocationsMapWithNamesState);
  const {bus} = useContext(EventBusContext);

  const onResetPageable = () => {
    resetPageable();
    setAssignable({0: []});
  };

  const onChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
    localStorage.setItem('serviceRequestsToAssignPerPage', e?.target.value as string);
    setAssignable({0: []});
    handleChangeRowsPerPage(e);
  };

  useEffect(() => {
    setFilters(filtersSets);
    setCurrentFilters(currFiltersSet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state === 'hasValue') setFiltersSets(contents);

    if (taskLocationsState === 'hasValue') setTaskLocations(taskLocationsContents);

    if (state === 'hasError')
      bus?.emit('error', [
        contents,
        {
          toastId: 'taskFilterSets',
        },
      ]);

    if (taskLocationsState === 'hasError')
      bus?.emit('error', [
        taskLocationsContents,
        {
          toastId: 'taskLocations',
        },
      ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <>
      <Paper sx={{paddingY: 1, paddingX: 2}}>
        <Filters
          isLoading={state === 'loading'}
          filterType="SERVICE_REQUEST"
          filtersState={filtersServiceRequestsToAssignState}
          filtersSetsState={filtersServiceRequestsSetsState}
          currentFiltersSetState={currentServiceRequestsToAssignFiltersSetState}
          filterBy={filterServiceRequestsBy}
          triggerQuery={triggerQuery}
          resetPageable={onResetPageable}
          dicts={{
            taskLocationCode: mapWithTaskLocations,
            status: new Map(Object.entries(statusCodeTranslation['serviceRequest'])),
          }}
          predefined={{
            taskLocationCode: Array.isArray(taskLocationsContents?.content)
              ? taskLocationsContents.content.map((item: ITaskLocationsDict) => [item.name, item.code])
              : taskLocationsContents,
            status: statusCodesAssign,
            serviceType: loadableWithDefault<IDeviceTypesDict>(deviceTypesLoadable, []).map((item) => [
              item.name,
              item.code,
            ]),
          }}
          dictSearch={{
            city: {
              searchStrategy: (str) => searchAddressItemStrategy(str, 'CITY'),
            },
            region: {
              searchStrategy: (str) => searchAddressItemStrategy(str, 'REGION'),
            },
            street: {
              searchStrategy: (str) => searchAddressItemStrategy(str, 'STREET'),
            },
            workerUid: {
              searchStrategy: (str) => searchWorkersForFilterStrategy(str),
            },
          }}
        />
      </Paper>
      <Paper>
        <ServiceRequestsTable
          page={page}
          size={size}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={onChangeRowsPerPage}
        />
      </Paper>
    </>
  );
};
